/**
 * Ссылка для клиента.
 *
 * Позволяет отдать заказчику готовые работы, не заводя ему аккаунт
 * и не перекачивая файлы через себя. Он видит только принятые видео
 * этого ТЗ — ни цен, ни исполнителей, ни чужих заказов.
 */
function ShareBlock({ taskId, doneCount }: { taskId: string; doneCount: number }) {
  const qc = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [days, setDays] = useState(30);

  type Link = {
    id: string;
    token: string;
    expiresAt: string | null;
    downloadCount: number;
    hasPassword: boolean;
  };

  const { data: links } = useQuery({
    queryKey: ['share-links', taskId],
    queryFn: () => api.get<Link[]>(`/api/share-links/task/${taskId}`),
  });

  const create = useMutation({
    mutationFn: () => api.post('/api/share-links', { taskId, expiresInDays: days }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['share-links', taskId] });
      setCreating(false);
      toast('Ссылка создана');
    },
    onError: (e: Error) => toast(e.message, '⚠'),
  });

  const revoke = useMutation({
    mutationFn: (linkId: string) => api.del(`/api/share-links/${linkId}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['share-links', taskId] });
      toast('Ссылка отозвана — по ней больше ничего не откроется');
    },
  });

  const urlOf = (token: string) => `${window.location.origin}/s/${token}`;

  return (
    <div className="card">
      {links?.length ? (
        <div className="space-y-2.5 mb-3">
          {links.map((l) => (
            <div key={l.id} className="bg-bg rounded-lg px-3 py-2.5">
              <div className="mono text-[11.5px] text-accent2 break-all leading-snug">
                {urlOf(l.token)}
              </div>
              <div className="flex items-center gap-2 mt-2">
                <span className="text-[11px] text-muted2 flex-1">
                  скачано: {l.downloadCount}
                  {l.expiresAt &&
                    ` · до ${new Date(l.expiresAt).toLocaleDateString('ru-RU')}`}
                </span>
                <button
                  className="btn btn-sm"
                  onClick={() => {
                    void navigator.clipboard.writeText(urlOf(l.token));
                    toast('Ссылка скопирована', '⧉');
                  }}
                >
                  Копировать
                </button>
                <button
                  className="btn btn-sm text-dang"
                  onClick={() => revoke.mutate(l.id)}
                  title="Ссылка перестанет работать"
                >
                  Отозвать
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-[12.5px] text-muted2 leading-relaxed mb-3">
          Клиент откроет ссылку без входа и скачает {doneCount}{' '}
          {plural(doneCount, 'готовую работу', 'готовые работы', 'готовых работ')} с обложками.
          Остального он не увидит.
        </div>
      )}

      {creating ? (
        <div className="space-y-2">
          <label className="label">Срок действия, дней</label>
          <input
            className="input mono"
            type="number"
            min={1}
            max={365}
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
          />
          <div className="flex gap-2">
            <button className="btn btn-sm flex-1" onClick={() => setCreating(false)}>
              Отмена
            </button>
            <button
              className="btn btn-sm btn-primary flex-1"
              onClick={() => create.mutate()}
              disabled={create.isPending}
            >
              Создать
            </button>
          </div>
        </div>
      ) : (
        <button className="btn btn-sm w-full" onClick={() => setCreating(true)}>
          + Создать ссылку
        </button>
      )}
    </div>
  );
}

/**
 * Замена исполнителя в действующем ТЗ.
 *
 * Меняет человека сразу во всех незавершённых видео. Принятые и
 * отменённые не трогаются — там работа уже закрыта.
 *
 * Про деньги: начисление уходит тому, кто числится исполнителем в момент
 * приёмки. Значит если работа частично сделана, а человека заменили,
 * заплатит система новому. Поэтому здесь стоит предупреждение —
 * молча такое делать нельзя.
 */
function ChangePerson({
  role,
  current,
  candidates,
  affected,
  alreadyWorked,
  onApply,
  pending,
}: {
  role: 'worker' | 'editor';
  current: { id: string; displayName: string } | null;
  candidates: TeamMember[];
  affected: number;
  alreadyWorked: number;
  onApply: (userId: string) => void;
  pending: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [pick, setPick] = useState('');

  const free = candidates.filter((c) => c.id !== current?.id);
  const label = role === 'worker' ? 'съёмщика' : 'монтажера';

  if (!open) {
    return (
      <button className="btn btn-sm w-full" onClick={() => { setOpen(true); setPick(''); }}>
        Сменить {label}
      </button>
    );
  }

  return (
    <div className="space-y-2 bg-bg rounded-lg p-3">
      <div className="label">Кому передать</div>
      <select className="input" value={pick} onChange={(e) => setPick(e.target.value)} autoFocus>
        <option value="">— выбрать —</option>
        {free.map((c) => (
          <option key={c.id} value={c.id}>
            {c.displayName}
            {typeof c.activeVideos === 'number' ? ` · в работе ${c.activeVideos}` : ''}
          </option>
        ))}
      </select>

      {free.length === 0 && (
        <div className="text-[12px] text-muted2">
          Больше некому — в команде нет других людей с этой ролью.
        </div>
      )}

      <div className="text-[12px] text-muted2 leading-relaxed">
        Заменится в <b className="text-fg">{affected}</b>{' '}
        {plural(affected, 'видео', 'видео', 'видео')} из тех, что ещё в работе.
        Принятые останутся на прежнем человеке.
      </div>

      {alreadyWorked > 0 && (
        <div className="text-[12px] text-warn leading-relaxed">
          ⚠ По {alreadyWorked}{' '}
          {plural(alreadyWorked, 'видео работа уже начата', 'видео работа уже начата', 'видео работа уже начата')}.
          Деньги за них при приёмке уйдут новому человеку — со старым рассчитайся
          вручную через корректировку в финансах.
        </div>
      )}

      <div className="flex gap-2">
        <button className="btn btn-sm flex-1" onClick={() => setOpen(false)}>
          Отмена
        </button>
        <button
          className="btn btn-sm btn-primary flex-1"
          disabled={!pick || pending}
          onClick={() => { onApply(pick); setOpen(false); }}
        >
          {pending ? 'Меняю…' : 'Заменить'}
        </button>
      </div>
    </div>
  );
}

/**
 * Добавление видео в существующее ТЗ.
 *
 * Смысл в одной ссылке для заказчика: доснятое сверх плана должно
 * попасть в тот же заказ, а не породить второе ТЗ и второй адрес.
 *
 * Работает и с завершённым ТЗ — оно само вернётся в работу, пока
 * добавленное не примут.
 */
function AddVideos({
  task,
  workers,
  onDone,
}: {
  task: Task;
  workers: TeamMember[];
  onDone: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState(1);
  const [ready, setReady] = useState(true);
  const [workerId, setWorkerId] = useState('');
  const [priceShoot, setPriceShoot] = useState(0);
  const [priceEdit, setPriceEdit] = useState(0);

  const add = useMutation({
    mutationFn: () =>
      api.post<{ added: number; from: number }>(`/api/tasks/${task.id}/videos`, {
        count,
        alreadyDone: ready,
        workerId: workerId || undefined,
        priceShoot,
        priceEdit,
      }),
    onSuccess: (res) => {
      onDone();
      setOpen(false);
      toast(
        res.added === 1
          ? `Добавлено видео ${res.from}`
          : `Добавлено видео с ${res.from} по ${res.from + res.added - 1}`,
      );
    },
    onError: (e: Error) => toast(e.message, '⚠'),
  });

  if (!open) {
    return (
      <button className="btn btn-sm w-full" onClick={() => setOpen(true)}>
        + Добавить видео
      </button>
    );
  }

  return (
    <div className="space-y-3 bg-bg rounded-lg p-3">
      <div>
        <div className="label">Сколько добавить</div>
        <input
          className="input mono"
          type="number"
          min={1}
          max={50}
          value={count}
          onChange={(e) => setCount(Math.max(1, Number(e.target.value)))}
          autoFocus
        />
        <div className="text-[11.5px] text-muted2 mt-1">
          Пойдут номерами с {(task.videos?.length ?? 0) + 1}
        </div>
      </div>

      <label className="flex items-start gap-2.5 cursor-pointer">
        <input
          type="checkbox"
          className="mt-0.5"
          checked={ready}
          onChange={(e) => setReady(e.target.checked)}
        />
        <span className="text-[12.5px] leading-relaxed">
          <b>Файл уже готов</b>
          <span className="text-muted2">
            {' — '}
            {ready
              ? 'загрузишь готовое видео, и оно примется само, без очереди приёмки'
              : 'обычный путь: исполнитель снимает, потом приёмка'}
          </span>
        </span>
      </label>

      {!ready && (
        <div>
          <div className="label">Кому поручить</div>
          <select
            className="input"
            value={workerId}
            onChange={(e) => setWorkerId(e.target.value)}
          >
            <option value="">{task.worker.displayName} — как в ТЗ</option>
            {workers
              .filter((w) => w.id !== task.worker.id)
              .map((w) => (
                <option key={w.id} value={w.id}>
                  {w.displayName}
                </option>
              ))}
          </select>
        </div>
      )}

      <div className="grid grid-cols-2 gap-2">
        <div>
          <div className="label">Съёмка, ₽</div>
          <input
            className="input mono"
            type="number"
            min={0}
            value={priceShoot}
            onChange={(e) => setPriceShoot(Math.max(0, Number(e.target.value)))}
          />
        </div>
        <div>
          <div className="label">Монтаж, ₽</div>
          <input
            className="input mono"
            type="number"
            min={0}
            value={priceEdit}
            onChange={(e) => setPriceEdit(Math.max(0, Number(e.target.value)))}
          />
        </div>
      </div>
      <div className="text-[11.5px] text-muted2 leading-relaxed">
        {priceShoot + priceEdit === 0
          ? 'При нуле начислений не будет вообще — так и надо, если доснял сам.'
          : `При приёмке начислится ${(priceShoot + priceEdit) * count} ₽`}
      </div>

      <div className="flex gap-2">
        <button className="btn btn-sm flex-1" onClick={() => setOpen(false)}>
          Отмена
        </button>
        <button
          className="btn btn-sm btn-primary flex-1"
          disabled={add.isPending}
          onClick={() => add.mutate()}
        >
          {add.isPending ? 'Добавляю…' : 'Добавить'}
        </button>
      </div>
    </div>
  );
}

/**
 * Карточка ТЗ.
 *
 * У ТЗ нет собственного статуса — есть прогресс по входящим видео.
 * Поэтому главное здесь список видео: видно, что готово, что зависло
 * и на ком именно.
 */
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Page, PageHeader } from '../components/Layout';
import { Loading, OverduePill, Person, Pill, Progress, Row, toast } from '../components/ui';
import { dateTime, money, plural, untilDeadline } from '../lib/format';
import type { Task, TeamMember } from '../lib/types';

export function TaskDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { can } = useAuth();
  const qc = useQueryClient();
  const [closing, setClosing] = useState(false);
  const [reason, setReason] = useState('');
  const [renaming, setRenaming] = useState(false);
  const [draftTitle, setDraftTitle] = useState('');
  const [pricing, setPricing] = useState(false);
  const [newShoot, setNewShoot] = useState(0);
  const [newEdit, setNewEdit] = useState(0);

  const { data: task, isLoading } = useQuery({
    queryKey: ['task', id],
    queryFn: () => api.get<Task>(`/api/tasks/${id}`),
    enabled: Boolean(id),
  });

  // Команда нужна для замены исполнителей и для добавления видео
  const { data: team } = useQuery({
    queryKey: ['team'],
    queryFn: () => api.get<TeamMember[]>('/api/users'),
    enabled: can('video.reassign') || can('task.create'),
  });

  /** Правка ТЗ: название и замена исполнителей ходят одним роутом */
  const patch = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.patch(`/api/tasks/${id}`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['task', id] });
      void qc.invalidateQueries({ queryKey: ['tasks'] });
    },
    onError: (e: Error) => toast(e.message, '⚠'),
  });

  const pin = useMutation({
    mutationFn: (input: { pinned: boolean; scope: 'all' | 'final_only' }) =>
      api.post(`/api/files/task/${id}/pin`, input),
    onSuccess: (_r, input) => {
      void qc.invalidateQueries({ queryKey: ['task', id] });
      toast(
        !input.pinned
          ? 'Откреплено'
          : input.scope === 'final_only'
            ? 'Сохранены финалы и обложки'
            : 'Сохранено всё, включая исходники',
        input.pinned ? '📌' : '○',
      );
    },
    onError: (e: Error) => toast(e.message, '⚠'),
  });

  const repeat = useMutation({
    mutationFn: () => api.post(`/api/tasks/${id}/repeat`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['tasks'] });
      toast('Создана копия ТЗ');
    },
    onError: (e: Error) => toast(e.message, '⚠'),
  });

  /**
   * Досрочное закрытие.
   *
   * Отменяет только то, что ещё в работе. Уже принятые видео не трогаются —
   * работа сделана и оплачена, отменять её задним числом нельзя.
   * Поэтому «закрыть» на 2 из 10 означает: два остаются, восемь снимаются.
   */
  const close = useMutation({
    mutationFn: (reason: string) => api.post<{ cancelledVideos: number }>(`/api/tasks/${id}/cancel`, { reason }),
    onSuccess: (res) => {
      void qc.invalidateQueries({ queryKey: ['task', id] });
      void qc.invalidateQueries({ queryKey: ['tasks'] });
      setClosing(false);
      setReason('');
      toast(`ТЗ закрыто · снято видео: ${res.cancelledVideos}`);
    },
    onError: (e: Error) => toast(e.message, '⚠'),
  });

  if (isLoading || !task) {
    return (
      <>
        <PageHeader title="ТЗ" back="/tasks" />
        <Page>
          <Loading />
        </Page>
      </>
    );
  }

  const videos = task.videos ?? [];
  /** Переставить можно всё, кроме принятого, опубликованного и отменённого */
  const reassignable = videos.filter(
    (v) => !['DONE', 'PUBLISHED', 'CANCELLED'].includes(v.status),
  ).length;
  /** Съёмка уже началась: человек либо снимает, либо материал сдан дальше */
  const shootingStarted = videos.filter((v) =>
    ['SHOOTING', 'AWAITING_EDIT', 'EDITING', 'REVIEW', 'REVISION'].includes(v.status),
  ).length;
  /** Монтаж уже начался */
  const editingStarted = videos.filter((v) =>
    ['EDITING', 'REVIEW', 'REVISION'].includes(v.status),
  ).length;

  const canReassign = can('video.reassign') && !task.cancelledAt && reassignable > 0;
  const active = team?.filter((u) => u.status === 'ACTIVE') ?? [];
  const workers = active.filter((u) => u.roles.includes('worker'));
  const editors = active.filter((u) => u.roles.includes('editor'));

  return (
    <>
      <PageHeader
        title={task.title}
        sub={`ТЗ #${task.number} · создано ${dateTime(task.createdAt)}`}
        back="/tasks"
        actions={
          can('task.create') && (
            <div className="flex gap-2">
              {can('task.edit') && !task.cancelledAt && (
                <button
                  className="btn"
                  onClick={() => {
                    setDraftTitle(task.title);
                    setRenaming((v) => !v);
                  }}
                >
                  Переименовать
                </button>
              )}
              <button className="btn" onClick={() => repeat.mutate()} disabled={repeat.isPending}>
                Повторить ТЗ
              </button>
              {task.progress.active > 0 && !task.cancelledAt && (
                <button className="btn btn-warn" onClick={() => setClosing((v) => !v)}>
                  Закрыть досрочно
                </button>
              )}
            </div>
          )
        }
      />
      <Page>
        {renaming && (
          <div className="card mb-4">
            <div className="font-bold mb-1">Название ТЗ</div>
            <div className="text-[12.5px] text-muted2 leading-relaxed mb-3">
              Видно в списке ТЗ, в карточках видео и в уведомлениях в боте.
              На шаблон и на уже созданные видео не влияет.
            </div>
            <input
              className="input mb-3"
              value={draftTitle}
              maxLength={120}
              onChange={(e) => setDraftTitle(e.target.value)}
              placeholder="Обзор техники · Айфоны"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter' && draftTitle.trim()) {
                  patch.mutate({ title: draftTitle.trim() });
                  setRenaming(false);
                  toast('Название изменено');
                }
                if (e.key === 'Escape') setRenaming(false);
              }}
            />
            <div className="flex gap-2">
              <button className="btn flex-1" onClick={() => setRenaming(false)}>
                Отмена
              </button>
              <button
                className="btn btn-primary flex-1"
                disabled={!draftTitle.trim() || patch.isPending}
                onClick={() => {
                  patch.mutate({ title: draftTitle.trim() });
                  setRenaming(false);
                  toast('Название изменено');
                }}
              >
                Сохранить
              </button>
            </div>
          </div>
        )}

        {closing && (
          <div className="card mb-4 border-warn/40 bg-warn/[0.05]">
            <div className="font-bold mb-1">Закрыть ТЗ досрочно</div>
            <div className="text-[12.5px] text-muted2 leading-relaxed mb-3">
              Снимется <b className="text-warn">{task.progress.active}</b> видео из тех,
              что ещё в работе. Уже принятые ({task.progress.done}) останутся —
              работа сделана и оплачена, отменять её задним числом нельзя.
            </div>
            <input
              className="input mb-3"
              placeholder="Причина: передумал, тема неактуальна, ошибся при создании…"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              autoFocus
            />
            <div className="flex gap-2">
              <button className="btn flex-1" onClick={() => setClosing(false)}>
                Отмена
              </button>
              <button
                className="btn btn-warn flex-1"
                onClick={() => close.mutate(reason.trim() || 'Закрыто досрочно')}
                disabled={close.isPending}
              >
                {close.isPending ? 'Закрываю…' : 'Закрыть ТЗ'}
              </button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-5 items-start">
          <div>
            <div className="card">
              <div className="flex items-center gap-3 mb-3">
                <span className="mono text-2xl font-extrabold">
                  {task.progress.done}/{task.progress.total}
                </span>
                <span className="text-muted text-[13px]">{task.progress.label}</span>
                <div className="flex-1" />
                {task.progress.overdue > 0 && (
                  <span className="pill bg-dang/10 text-dang">
                    просрочено: {task.progress.overdue}
                  </span>
                )}
              </div>
              <Progress percent={task.progress.percent} />
            </div>

            {task.comment && (
              <div className="card mt-3">
                <div className="sec">Комментарий</div>
                <div className="text-[14.5px] leading-relaxed whitespace-pre-wrap">
                  {task.comment}
                </div>
              </div>
            )}

            <div className="sec">Видео · {task.videos?.length ?? 0}</div>
            {can('task.create') && !task.cancelledAt && (
              <div className="mb-3">
                <AddVideos
                  task={task}
                  workers={workers}
                  onDone={() => {
                    void qc.invalidateQueries({ queryKey: ['task', id] });
                    void qc.invalidateQueries({ queryKey: ['tasks'] });
                  }}
                />
              </div>
            )}
            <div className="card p-0 overflow-hidden">
              {task.videos?.map((v) => {
                const d = untilDeadline(v.editDeadlineAt ?? v.deadlineShootAt);
                const isActive = !['DONE', 'CANCELLED', 'PUBLISHED'].includes(v.status);
                return (
                  <Link
                    key={v.id}
                    to={`/videos/${v.id}`}
                    className="flex items-center gap-4 px-4 py-3.5 border-b border-[#1A1A24] last:border-0 hover:bg-white/[0.03] transition-colors"
                  >
                    <span className="mono text-muted2 w-8 text-[13px]">#{v.index}</span>
                    {d.overdue && isActive ? <OverduePill /> : <Pill status={v.status} />}
                    <div className="flex-1 min-w-0 text-[12.5px] text-muted truncate">
                      {v.worker.displayName}
                      {v.editor && ` → ${v.editor.displayName}`}
                    </div>
                    {isActive && (
                      <span className={`text-xs ${d.overdue ? 'text-dang' : 'text-muted2'}`}>
                        {d.text}
                      </span>
                    )}
                    {v.revisionCount > 0 && (
                      <span className="text-[11px] text-warn">
                        доработок: {v.revisionCount}
                      </span>
                    )}
                  </Link>
                );
              })}
            </div>
          </div>

          <div>
            <div className="sec !mt-0">Условия</div>
            <div className="card">
              <Row label="Цена съёмки">
                <span className="mono">{money(task.priceShoot)}</span>
              </Row>
              <Row label="Цена монтажа">
                <span className="mono">{money(task.priceEdit)}</span>
              </Row>
              <Row label="Всего по ТЗ">
                <span className="mono">
                  {money((task.priceShoot + task.priceEdit) * task.quantity)}
                </span>
              </Row>
              <Row label="Дедлайн съёмки">{dateTime(task.deadlineShootAt)}</Row>
              <Row label="Срок монтажа">{task.editSlaHours} ч после материала</Row>
              {task.repeatActive && (
                <Row label="Повтор">каждые {task.repeatEveryDays} дн.</Row>
              )}

              {can('task.edit') && !task.cancelledAt && (
                <div className="border-t border-[#1A1A24] mt-3 pt-3">
                  {pricing ? (
                    <div className="space-y-2">
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <div className="label">Съёмка, ₽</div>
                          <input
                            className="input mono"
                            type="number"
                            min={0}
                            value={newShoot}
                            onChange={(e) => setNewShoot(Math.max(0, Number(e.target.value)))}
                            autoFocus
                          />
                        </div>
                        <div>
                          <div className="label">Монтаж, ₽</div>
                          <input
                            className="input mono"
                            type="number"
                            min={0}
                            value={newEdit}
                            onChange={(e) => setNewEdit(Math.max(0, Number(e.target.value)))}
                          />
                        </div>
                      </div>
                      <div className="text-[12px] text-muted2 leading-relaxed">
                        Применится к <b className="text-fg">{reassignable}</b>{' '}
                        {plural(reassignable, 'видео', 'видео', 'видео')}, которые ещё
                        не приняты. Уже принятым цены не меняем — за них деньги
                        начислены, и переписывать их задним числом нельзя.
                      </div>
                      <div className="flex gap-2">
                        <button className="btn btn-sm flex-1" onClick={() => setPricing(false)}>
                          Отмена
                        </button>
                        <button
                          className="btn btn-sm btn-primary flex-1"
                          disabled={patch.isPending}
                          onClick={() => {
                            patch.mutate({ priceShoot: newShoot, priceEdit: newEdit });
                            setPricing(false);
                            toast('Ставки изменены');
                          }}
                        >
                          Сохранить
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      className="btn btn-sm w-full"
                      onClick={() => {
                        setNewShoot(Math.round(task.priceShoot / 100));
                        setNewEdit(Math.round(task.priceEdit / 100));
                        setPricing(true);
                      }}
                    >
                      Изменить ставки
                    </button>
                  )}
                </div>
              )}
            </div>

            <div className="sec">Исполнители</div>
            <div className="card space-y-3">
              <Person
                id={task.worker.id}
                name={task.worker.displayName}
                url={task.worker.avatarUrl}
                sub="съёмка"
              />
              {canReassign && (
                <ChangePerson
                  role="worker"
                  current={task.worker}
                  candidates={workers}
                  affected={reassignable}
                  alreadyWorked={shootingStarted}
                  pending={patch.isPending}
                  onApply={(workerId) => {
                    patch.mutate({ workerId });
                    toast('Съёмщик заменён');
                  }}
                />
              )}

              {task.editor ? (
                <Person
                  id={task.editor.id}
                  name={task.editor.displayName}
                  url={task.editor.avatarUrl}
                  sub="монтаж"
                />
              ) : (
                <div className="text-[12.5px] text-muted2">
                  {task.mode === 'SOLO'
                    ? 'Без монтажера — всё делает один человек'
                    : 'Монтажер назначится автоматически'}
                </div>
              )}
              {canReassign && task.mode !== 'SOLO' && (
                <ChangePerson
                  role="editor"
                  current={task.editor}
                  candidates={editors}
                  affected={reassignable}
                  alreadyWorked={editingStarted}
                  pending={patch.isPending}
                  onApply={(editorId) => {
                    patch.mutate({ editorId });
                    toast('Монтажер заменён');
                  }}
                />
              )}
            </div>

            {can('task.create') && task.progress.done > 0 && (
              <>
                <div className="sec">Ссылка для клиента</div>
                <ShareBlock taskId={task.id} doneCount={task.progress.done} />
              </>
            )}

            {can('file.pin') && (
              <>
                <div className="sec">Файлы</div>
                <div className="card">
                  <div className="text-[12.5px] text-muted2 leading-relaxed mb-3">
                    {task.keepFiles
                      ? 'ТЗ сохранено целиком — не удалятся даже исходники.'
                      : 'Готовые видео, прокси и обложки хранятся бессрочно. Удалятся только исходники — через две недели после приёмки каждого видео.'}
                  </div>
                  {task.keepFiles ? (
                    <button
                      className="btn btn-sm w-full"
                      onClick={() => pin.mutate({ pinned: false, scope: 'all' })}
                      disabled={pin.isPending}
                    >
                      Снять сохранение
                    </button>
                  ) : (
                    <div className="space-y-2">
                      <button
                        className="btn btn-sm w-full"
                        onClick={() => pin.mutate({ pinned: true, scope: 'final_only' })}
                        disabled={pin.isPending}
                      >
                        📌 Сохранить финалы и обложки
                      </button>
                      <button
                        className="btn btn-sm w-full"
                        onClick={() => pin.mutate({ pinned: true, scope: 'all' })}
                        disabled={pin.isPending}
                      >
                        📌 Сохранить всё с исходниками
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </Page>
    </>
  );
}
